import { useEffect, useState } from "react";

/**
 * How long the rail's overlay panels hold a placeholder before revealing their
 * list, in milliseconds.
 *
 * Unlike the rail's own skeleton, this one does **not** correspond to a fetch.
 * Both panels render from state that is already present by the time their
 * trigger is clickable — the product list from `userProfile.ProductList`, the
 * partner list from the mount-time fetch in `usePartnerCustomerSelection` — so
 * nothing is in flight when they open. It is a deliberate, product-owned reveal
 * so the panels open with the same loading language as the rest of the rail.
 *
 * It lives here rather than in `sideMenuDensity`: that module is class strings
 * Tailwind extracts statically, and its contract test enforces exactly that.
 */
export const PANEL_REVEAL_MS = 250;

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Guarded for any non-browser render; the jsdom setup stubs it as no-reduce. */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/**
 * True while a just-opened overlay panel should show its spinner instead of its
 * list, for `PANEL_REVEAL_MS` after `open` flips true.
 *
 * Shared by the account card and the tenant selector so the two cannot drift
 * into different reveal lengths, and so "is this panel revealing?" has a single
 * definition rather than a timer per call site.
 *
 * The reveal is cosmetic (see `PANEL_REVEAL_MS`), so a reduced-motion preference
 * skips it rather than merely stilling the ring: `motion-reduce` freezes the
 * spinner, and a frozen spinner held for a quarter second reads as a panel that
 * hung — worse than the instant list the preference is asking for.
 */
export function usePanelReveal(open: boolean): boolean {
  /* Read once per mount. The preference does not change mid-interaction, and
     re-reading it every render would make the hook impure for no gain. */
  const [reduceMotion] = useState(prefersReducedMotion);
  /** False until this open-session's reveal has elapsed. */
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset on close so the next open replays the reveal instead of opening
      // straight into the list.
      setRevealed(false);
      return;
    }
    const timer = window.setTimeout(() => setRevealed(true), PANEL_REVEAL_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  /* Derived rather than stored: `revealed` starts false, so the spinner is
     already showing on the first render after `open` flips. Turning it on from
     an effect instead would paint one frame of the real list first — precisely
     the flash the reveal exists to avoid. */
  return open && !revealed && !reduceMotion;
}
